import { DepartmentCountryCatalog } from "./addressLine";
import { ADR_CLASSES } from "./constants";
import { PersonalEmailDomain } from "./personalEmail";
import { CargoDimensionMapper } from "./cargoDimensions";
import { DepartmentFieldAccess, DepartmentFormDefaults, type DepartmentFieldId } from "./departmentFields";
import { makeCargoLine } from "./formModel";
import type {
  DepartmentConfig,
  ExtractedCargo,
  ExtractedData,
  ExtractedTimeWindow,
  FormState,
  PalletType,
  TimeMode,
} from "./types";

const cargoDimensions = new CargoDimensionMapper();
const personalEmails = new PersonalEmailDomain();

function cargoQty(c: ExtractedCargo): number {
  if ((c.qty || 0) > 0) return c.qty as number;
  if (typeof c.ldm === "number" && c.ldm > 0) return 1;
  return 0;
}

function labelToType(c: ExtractedCargo): PalletType {
  const l = (c.type || "").toLowerCase();
  if (l === "ldm" || l.includes("unspecified") || l.includes("not specified")) return "unspecified";
  if (l.includes("half") || l.includes("1/2") || l.includes("\u00bd")) return "half";
  if (l.includes("custom")) return "custom";
  if (/120\s*[x\u00d7]\s*120/.test(l)) return "size120x120";
  if (/150\s*[x\u00d7]\s*80|80\s*[x\u00d7]\s*150/.test(l)) return "size150x80";
  if (/80\s*[x\u00d7]\s*60|60\s*[x\u00d7]\s*80/.test(l)) return "size80x60";
  if (l.includes("block")) return "block";
  if (l.includes("ibc")) return "ibc";
  if (l.includes("cp3") || l.includes("cp-3") || l.includes("cp 3")) return "cp3";
  if (l.includes("euro") || l.includes("eur")) return "euro";
  if (!l && typeof c.ldm === "number" && c.ldm > 0) return "unspecified";
  return "euro";
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

function asTime(value?: string | null): string {
  return value && TIME_PATTERN.test(value) ? value : "";
}

function asDate(value?: string | null): string {
  return value && DATE_PATTERN.test(value) ? value : "";
}

class ExtractedFieldGate {
  constructor(private readonly access: DepartmentFieldAccess | null) {}

  allows(id: DepartmentFieldId): boolean {
    return !this.access || this.access.isPresent(id);
  }

  allowsExtendedReferences(): boolean {
    return !this.access || this.access.hasExtendedReferences();
  }

  showsTimeDeliveryDeadline(): boolean {
    return !this.access || this.access.showsTimeDeliveryDeadline();
  }

  showsTemperature(): boolean {
    return !this.access || this.access.showsTemperature();
  }

  countryCatalog() {
    return this.access?.countryCatalog() ?? DepartmentCountryCatalog.fromCodes(undefined);
  }
}

interface TimePhase {
  type: TimeMode;
  from: string;
  until: string;
  fixed: string;
}

function readTimePhase(
  fixed?: string | null,
  from?: string | null,
  until?: string | null,
): TimePhase | null {
  const fixedAt = asTime(fixed);
  if (fixedAt) return { type: "fixed", from: "", until: "", fixed: fixedAt };
  const fromAt = asTime(from);
  if (fromAt) return { type: "window", from: fromAt, until: asTime(until), fixed: "" };
  return null;
}

export interface UnsupportedRouteWarning {
  loadCountry: string;
  unloadCountry: string;
}

export interface ApplyResult {
  state: FormState;
  filled: string[];
  // Form field ids that were filled (for the transient highlight in the form).
  filledIds: string[];
  missingIds: string[];
  unsupportedRoute: UnsupportedRouteWarning | null;
}

// Merge an extraction payload into the form; returns filled labels + field ids.
export function applyExtracted(
  data: ExtractedData,
  prev: FormState,
  config?: DepartmentConfig,
): ApplyResult {
  const next: FormState = {
    ...prev,
    adr: { ...prev.adr },
    temp: { ...prev.temp },
    tail: { ...prev.tail },
    ftl: { ...prev.ftl },
    time: { ...prev.time },
    ref: { ...prev.ref },
    attachments: { active: prev.attachments.active, files: prev.attachments.files.slice() },
  };
  const filled: string[] = [];
  const filledIds: string[] = [];
  const missingIds: string[] = [];
  const markField = (id: string) => {
    filledIds.push(id);
  };
  const miss = (id: string) => {
    missingIds.push(id);
  };
  const mark = (label: string, id: string) => {
    filled.push(label);
    markField(id);
  };
  const markOrMiss = (id: string, present: boolean) => {
    if (present) markField(id);
    else miss(id);
  };
  const access = config ? new DepartmentFieldAccess(config) : null;
  const gate = new ExtractedFieldGate(access);

  const catalog = gate.countryCatalog();
  const loadRaw = data.loadCountry?.trim() ?? "";
  const unloadRaw = data.unloadCountry?.trim() ?? "";
  const loadCountry = catalog.toCode(data.loadCountry);
  const unloadCountry = catalog.toCode(data.unloadCountry);
  const home = access?.homeCountry() ?? "";
  const unsupportedRoute =
    home && loadRaw && unloadRaw && loadCountry !== home && unloadCountry !== home
      ? { loadCountry: loadRaw, unloadCountry: unloadRaw }
      : null;
  if (data.loadCity) { next.loadCity = data.loadCity; mark("Loading city", "loadCity"); }
  if (!unsupportedRoute && loadCountry) {
    next.loadCountry = loadCountry;
    mark("Loading country", "loadCountry");
  }
  if (data.loadPostal) { next.loadPostal = data.loadPostal; mark("Loading postal", "loadPostal"); }
  if (gate.allows("loadStreet") && data.loadStreet) {
    next.loadStreet = data.loadStreet;
    mark("Loading street", "loadStreet");
  }
  if (gate.allows("loadHouseNumber") && data.loadHouseNumber) {
    next.loadHouseNumber = data.loadHouseNumber;
    mark("Loading street number", "loadHouseNumber");
  }
  if (data.unloadCity) { next.unloadCity = data.unloadCity; mark("Unloading city", "unloadCity"); }
  if (!unsupportedRoute && unloadCountry) {
    next.unloadCountry = unloadCountry;
    mark("Unloading country", "unloadCountry");
  }
  if (data.unloadPostal) { next.unloadPostal = data.unloadPostal; mark("Unloading postal", "unloadPostal"); }
  if (gate.allows("unloadStreet") && data.unloadStreet) {
    next.unloadStreet = data.unloadStreet;
    mark("Unloading street", "unloadStreet");
  }
  if (gate.allows("unloadHouseNumber") && data.unloadHouseNumber) {
    next.unloadHouseNumber = data.unloadHouseNumber;
    mark("Unloading street number", "unloadHouseNumber");
  }
  const unloadDate = asDate(data.unloadDate);
  if (gate.allows("unloadDate") && unloadDate) {
    next.unloadDate = unloadDate;
    mark("Preferred Unloading date", "unloadDate");
  }
  if (data.weight) { next.totalWeight = String(data.weight); mark("Weight", "totalWeight"); }
  if (data.goods) { next.goods = data.goods; mark("Goods", "goods"); }
  const pickupDate = asDate(data.pickupDate);
  if (pickupDate) { next.pickupDate = pickupDate; mark("Pickup date", "pickupDate"); }
  if (data.company) { next.company = data.company; mark("Company", "company"); }
  if (data.email && !personalEmails.isPersonal(data.email)) {
    next.email = data.email;
    mark("Email", "email");
  }

  const validCargo = (data.cargo || []).filter((c) => cargoQty(c) > 0);
  if (validCargo.length > 0) {
    next.cargo = validCargo.map((c) => {
      const line = makeCargoLine();
      line.type = labelToType(c);
      line.qty = String(cargoQty(c));
      if (line.type === "half") {
        return cargoDimensions.applyType({ ...line, stack: c.stackable === true }, "half");
      }
      if (line.type === "custom") {
        if (c.length) line.length = String(c.length);
        if (c.width) line.width = String(c.width);
      }
      if (c.height) line.height = String(c.height);
      if (typeof c.ldm === "number" && c.ldm > 0) line.ldm = String(c.ldm);
      const hasDims =
        Boolean(c.height) ||
        (line.type === "custom" && (Boolean(c.length) || Boolean(c.width)));
      if (line.ldm && !hasDims) line.sizeMode = "ldm";
      line.stack = c.stackable === true;
      return line;
    });
    filled.push("Cargo details");
    validCargo.forEach((c, index) => {
      const line = next.cargo[index];
      markField(`cargo-${line.id}-qty`);
      if ((c.type || "").trim()) markField(`cargo-${line.id}-type`);
      else markOrMiss(`cargo-${line.id}-type`, false);
      if (line.type !== "half" && line.sizeMode === "ldm") {
        markField(`cargo-${line.id}-sizeMode`);
        if (line.ldm) markField(`cargo-${line.id}-ldm`);
      }
      if (line.height) markField(`cargo-${line.id}-height`);
      markOrMiss(`cargo-${line.id}-stack`, c.stackable === true || c.stackable === false);
    });
  }

  const tw: ExtractedTimeWindow = data.timeWindow ?? {};
  const loadPhase = readTimePhase(tw.loadFixed, tw.loadFrom, tw.loadUntil);
  const unloadPhase = readTimePhase(tw.unloadFixed, tw.unloadFrom, tw.unloadUntil);
  const deliveryDate = asDate(tw.deliveryDate);
  const keepDeadline = gate.showsTimeDeliveryDeadline();
  if (!keepDeadline && deliveryDate && !next.unloadDate) {
    next.unloadDate = deliveryDate;
    mark("Preferred Unloading date", "unloadDate");
  }
  const useDeadline = keepDeadline && Boolean(deliveryDate);
  if (loadPhase || unloadPhase || useDeadline) {
    next.time.active = true;
    if (loadPhase) {
      next.time.loadType = loadPhase.type;
      next.time.loadFrom = loadPhase.from;
      next.time.loadUntil = loadPhase.until;
      next.time.loadFixed = loadPhase.fixed;
    }
    if (unloadPhase) {
      next.time.unloadType = unloadPhase.type;
      next.time.unloadFrom = unloadPhase.from;
      next.time.unloadUntil = unloadPhase.until;
      next.time.unloadFixed = unloadPhase.fixed;
    }
    if (useDeadline) next.time.deliveryDate = deliveryDate;
    filled.push("Time window");
    if (keepDeadline) markOrMiss("deliveryDate", !!next.time.deliveryDate);
    markOrMiss("loadTime", !!loadPhase);
    markOrMiss("unloadTime", !!unloadPhase);
  }

  const s = data.surcharges;
  if (s) {
    if (s.adr) {
      next.adr.active = true;
      const cn = typeof s.adrClass === "string" ? parseInt(s.adrClass, 10) : s.adrClass;
      if (cn && cn >= 1 && cn <= 9) next.adr.adrClass = ADR_CLASSES[cn - 1];
      if (s.adrUn) next.adr.un = String(s.adrUn).replace(/\D/g, "");
      filled.push("ADR");
      markOrMiss("adrClass", !!next.adr.adrClass);
      markOrMiss("adrUn", !!next.adr.un);
    }
    if (gate.showsTemperature() && s.temperature) {
      next.temp.active = true;
      if (typeof s.tempMin === "number") next.temp.min = String(s.tempMin);
      if (typeof s.tempMax === "number") next.temp.max = String(s.tempMax);
      filled.push("Temperature");
      markOrMiss("tempMin", next.temp.min !== "");
      markOrMiss("tempMax", next.temp.max !== "");
    }
    if (s.ftl) {
      next.ftl.active = true;
      filled.push("FTL");
      markField("ftl");
    }
    if (s.tailLiftLoad || s.tailLiftUnload) {
      next.tail.active = true;
      next.tail.load = !!s.tailLiftLoad;
      next.tail.unload = !!s.tailLiftUnload;
      filled.push("Tail lift");
      markOrMiss("tailLoad", next.tail.load);
      markOrMiss("tailUnload", next.tail.unload);
    }
    const loadReference = gate.allowsExtendedReferences() ? s.loadReference : undefined;
    const unloadReference = gate.allowsExtendedReferences() ? s.unloadReference : undefined;
    if (s.reference || loadReference || unloadReference || s.notes) {
      next.ref.active = true;
      if (s.reference) next.ref.reference = s.reference;
      if (loadReference) next.ref.loadReference = loadReference;
      if (unloadReference) next.ref.unloadReference = unloadReference;
      if (s.notes) next.ref.notes = s.notes;
      filled.push("Reference/Notes");
      markOrMiss("refReference", !!next.ref.reference);
      if (loadReference) markField("refLoadReference");
      if (unloadReference) markField("refUnloadReference");
      markOrMiss("refNotes", !!next.ref.notes);
    }
  }

  if (unsupportedRoute) {
    next.loadCountry = "";
    next.unloadCountry = "";
    miss("loadCountry");
    miss("unloadCountry");
  }

  const state = access ? new DepartmentFormDefaults(access).apply(next) : next;
  return { state, filled, filledIds, missingIds, unsupportedRoute };
}
